import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Lock } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field, PrimaryBtn, inputCls } from "@/components/Common";
import { useAuth } from "@/context/AuthContext";
import { errMsg, http, inr, login } from "@/lib/api";
import { payWithRazorpay } from "@/lib/pay";

export const BookingDialog = ({ open, onOpenChange, booking, title, lines = [], amount }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ contact_name: "", contact_email: "", contact_phone: "" });
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState("");
  const [applied, setApplied] = useState(null);
  const [checking, setChecking] = useState(false);
  const total = applied ? applied.total : amount;

  useEffect(() => {
    if (!open) { setApplied(null); setCode(""); }
  }, [open]);

  const applyCoupon = async () => {
    if (!code.trim()) return;
    setChecking(true);
    try {
      const { data } = await http.post("/bookings/quote", { ...booking, contact_name: "Guest", contact_email: "guest@example.com", contact_phone: "0000000000", coupon_code: code });
      setApplied({ ...data, code: data.coupon.code });
      toast.success(`${data.coupon.code} applied — you save ${inr(data.discount)}`);
    } catch (err) {
      setApplied(null);
      toast.error(errMsg(err));
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, contact_name: f.contact_name || user.name, contact_email: f.contact_email || user.email }));
  }, [user]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!user) return login();
    setBusy(true);
    try {
      const { data } = await http.post("/bookings", { ...booking, ...form, coupon_code: applied?.code || null });
      if (data.payment) {
        const result = await payWithRazorpay(data.payment, data.booking.id);
        if (result === "confirmed") toast.success(`Payment successful! Booking ${data.booking.ref} confirmed.`);
        else toast.message(`Booking ${data.booking.ref} saved. You can complete payment from My Bookings.`);
      } else {
        toast.success(`Booking ${data.booking.ref} reserved. Our team will contact you to complete payment.`);
      }
      onOpenChange(false);
      navigate("/my-bookings");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-lg overflow-y-auto" data-testid="booking-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{title}</DialogTitle>
          <DialogDescription>Review your trip and enter traveller contact details.</DialogDescription>
        </DialogHeader>
        <div className="rounded-xl bg-[#F4F0EA] p-4 text-sm space-y-1.5">
          {lines.map(([k, v]) => (
            <div key={k} className="flex justify-between gap-4"><span className="text-slate-500">{k}</span><span className="text-right font-medium">{v}</span></div>
          ))}
          {applied && (
            <>
              <div className="flex justify-between border-t border-[#E5E0D8] pt-2 mt-2"><span className="text-slate-500">Subtotal</span><span className="font-medium">{inr(applied.subtotal)}</span></div>
              <div className="flex justify-between text-emerald-700" data-testid="booking-discount"><span>Coupon {applied.code}</span><span className="font-semibold">− {inr(applied.discount)}</span></div>
            </>
          )}
          <div className="flex justify-between border-t border-[#E5E0D8] pt-2 mt-2 text-base">
            <span className="font-semibold">Total</span>
            <span className="font-bold text-[#C2603F]" data-testid="booking-total">{inr(total)}</span>
          </div>
        </div>
        <div className="flex gap-2">
          <input className={`${inputCls} uppercase`} placeholder="Coupon code (e.g. DIWALI15)" value={code} disabled={!!applied}
            onChange={(e) => setCode(e.target.value)} data-testid="booking-coupon-input" />
          {applied ? (
            <button type="button" onClick={() => { setApplied(null); setCode(""); }} className="h-11 rounded-lg border border-[#E5E0D8] px-4 text-sm font-semibold" data-testid="booking-coupon-remove">Remove</button>
          ) : (
            <button type="button" onClick={applyCoupon} disabled={checking} className="h-11 rounded-lg bg-[#0D1B2A] px-4 text-sm font-semibold text-white disabled:opacity-60" data-testid="booking-coupon-apply">
              {checking ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply"}
            </button>
          )}
        </div>
        {user ? (
          <form onSubmit={submit} className="space-y-3">
            <Field label="Full name"><input required className={inputCls} value={form.contact_name} onChange={set("contact_name")} data-testid="booking-name-input" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Email"><input required type="email" className={inputCls} value={form.contact_email} onChange={set("contact_email")} data-testid="booking-email-input" /></Field>
              <Field label="Mobile"><input required minLength={10} className={inputCls} value={form.contact_phone} onChange={set("contact_phone")} placeholder="+91 98xxxxxxx" data-testid="booking-phone-input" /></Field>
            </div>
            <PrimaryBtn type="submit" disabled={busy} className="w-full" data-testid="booking-confirm-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />} Book & pay {inr(total)}
            </PrimaryBtn>
            <p className="text-center text-xs text-slate-500">Secure payment via Razorpay · UPI, cards, netbanking</p>
          </form>
        ) : (
          <PrimaryBtn onClick={() => login()} className="w-full" data-testid="booking-login-btn">Sign in with Google to book</PrimaryBtn>
        )}
      </DialogContent>
    </Dialog>
  );
};
